/**
 * iter182 - Blog prefetch helper.
 *
 * Problem: on a fresh navigation to /blog, the user waits for
 *   1) the lazy-loaded Blog.jsx chunk to download,
 *   2) `/api/blog/posts` (10 KB gzipped, ~150-800 ms on prod) to respond,
 *   3) 84 cards to render.
 *
 * Fix: kick off (1) and (2) the moment the user opens the Resources
 * dropdown or hovers the "Blog" link. By the time they click, the chunk
 * is parsed and the payload is sitting in localStorage — Blog.jsx
 * hydrates instantly from the cache, and the background refresh is a
 * no-op if the cache is fresh.
 *
 * Cache lives in localStorage (survives tab close), not sessionStorage.
 * TTL is 30 min. Blog.jsx also writes to this key so the two flows share.
 */

import { API } from "@/lib/api";

const LS_KEY = "sl_blog_list:_all_";
const TTL_MS = 30 * 60 * 1000; // 30 min

let inflight = null;
let chunkPrefetched = false;

function isFresh(entry) {
  return (
    entry &&
    entry.ts &&
    Date.now() - entry.ts < TTL_MS &&
    Array.isArray(entry.data) &&
    entry.data.length > 0
  );
}

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch (_) {
    /* quota / private mode - ignore */
  }
}

/**
 * Warm both the JS chunk and the API payload.
 * Idempotent - safe to call on every hover / dropdown open.
 */
export function prefetchBlog() {
  // 1) Preload the lazy Blog.jsx bundle (once).
  if (!chunkPrefetched) {
    chunkPrefetched = true;
    // Fire-and-forget. Webpack will de-dupe with the later real import.
    import(/* webpackPrefetch: true */ "@/pages/Blog").catch(() => {
      chunkPrefetched = false; // allow a retry on failure
    });
  }

  // 2) If we already have a fresh cache, we're done.
  const cached = readCache();
  if (isFresh(cached)) return;

  // 3) Only one in-flight fetch at a time.
  if (inflight) return;

  inflight = fetch(`${API}/blog/posts`, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (Array.isArray(data) && data.length > 0) writeCache(data);
    })
    .catch(() => {
      /* silent - Blog.jsx has its own retry+error UI */
    })
    .finally(() => {
      inflight = null;
    });
}

export const BLOG_CACHE_KEY = LS_KEY;
export const BLOG_CACHE_TTL_MS = TTL_MS;
